import { useCopilotAction, useCopilotReadable } from "@copilotkit/react-core"
import { useDraftingStore } from "@/features/drafting/store/drafting.store"
import { useOrderDraftStore } from "@/features/order-draft/store/order-draft.store"
import { useAnalysisStore } from "@/features/analysis/store/analysis.store"
import { useThreadContext } from "@/providers/ThreadProvider"
import type { Allegation } from "@/types"

/**
 * Registers CopilotKit actions for the OIO workspace.
 *
 * NOTE: oio:workflow_progress and oio:draft_updated have been removed.
 * - Stage progress is now streamed via useCoAgent (AG-UI STATE_SNAPSHOT events).
 * - Draft content is now read from useOIOAgent().oio_draft (LangGraph state).
 */
export function useOIOActions() {
  const draftingStore = useDraftingStore()
  const orderDraftStore = useOrderDraftStore()
  const analysisStore = useAnalysisStore()
  const { activeThread } = useThreadContext()

  useCopilotAction({
    name: "oio:analysis_ready",
    description: "Set the allegation analysis data",
    parameters: [
      { name: "findings_summary", type: "object[]", description: "Array of allegation objects", required: false },
      { name: "summary", type: "string", description: "Overall analysis summary", required: false },
      { name: "scnSummary", type: "string", required: false },
      { name: "replySummary", type: "string", required: false },
      { name: "legalProvisions", type: "string[]", required: false },
    ],
    handler: (data: {
      findings_summary?: Allegation[]
      summary?: string
      scnSummary?: string
      replySummary?: string
      legalProvisions?: string[]
    }) => {
      analysisStore.setAnalysisData(data)
    },
  })

  useCopilotAction({
    name: "oio:review_required",
    description: "Signal that the agent needs user review before continuing",
    parameters: [
      { name: "reviewType", type: "string" },
      { name: "context", type: "object", required: false },
    ],
    handler: ({ reviewType, context }: { reviewType: string; context?: object }) => {
      draftingStore.setAwaitingReview({
        reviewType: reviewType as "section_review" | "general_review",
        context: (context ?? {}) as Record<string, unknown>,
      })
    },
  })

  useCopilotAction({
    name: "oio:session_error",
    description: "Handle a workflow error with optional resume capability",
    parameters: [
      { name: "message", type: "string" },
      { name: "stageName", type: "string", required: false },
      { name: "checkpointId", type: "string", required: false },
      { name: "resumeSupported", type: "boolean", required: false },
    ],
    handler: ({
      message,
      stageName,
      checkpointId,
      resumeSupported = false,
    }: {
      message: string
      stageName?: string
      checkpointId?: string
      resumeSupported?: boolean
    }) => {
      draftingStore.setError({ message, stageName, checkpointId, resumeSupported })
    },
  })

  useCopilotReadable({
    description: "The SCN (Show Cause Notice) filename for the active case",
    value: (activeThread?.metadata?.scnFileName as string) ?? null,
  })

  useCopilotReadable({
    description: "The Reply document filename for the active case",
    value: (activeThread?.metadata?.replyFileName as string) ?? null,
  })

  useCopilotReadable({
    description: "The current order draft content (markdown)",
    value: orderDraftStore.content || null,
  })
}
